/**
 * Vinculación con la app de impresión (md-printer-bridge).
 *
 * El asistente abre la app con `mdprinter://setData?...&requestId=...`. Al presionar
 * "Volver al tótem", la app escribe su configuración en
 * `kiosk/{env}/client/{client}/printer_links/{instanceId}` repitiendo el `request_id`, y vuelve
 * al navegador. El asistente escucha ese documento y valida lo que la app reporta.
 */
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../lib/firebase";

/** Primera versión de la app (versionCode) que tiene el botón "Volver al tótem". */
export const MIN_PRINTER_APP_VERSION_CODE = 5;
export const MIN_PRINTER_APP_VERSION_NAME = "4.1";

/**
 * Escucha la respuesta de la app de impresión a una solicitud de vinculación.
 *
 * @param {string} env
 * @param {string} clientId
 * @param {string} instanceId
 * @param {string} requestId - Solo se notifican documentos con este `request_id`.
 * @param {(status: Object) => void} onStatus
 * @returns {() => void} Cancela la escucha.
 */
export function watchPrinterLink(env, clientId, instanceId, requestId, onStatus) {
  const ref = doc(db, "kiosk", env, "client", clientId, "printer_links", instanceId);
  return onSnapshot(ref, (snapshot) => {
    const status = snapshot.exists() ? snapshot.data() : null;
    if (status?.request_id === requestId) onStatus(status);
  }, (error) => {
    console.error("[PrinterLink] Error listening to printer link:", error);
  });
}

/**
 * Valida la configuración reportada por la app de impresión.
 *
 * @returns {{ok: true} | {ok: false, error: 'mismatch' | 'outdated' | 'no_printer' | 'printing_disabled'}}
 */
export function validatePrinterLink(status, { env, clientId, instanceId }) {
  if (status.environment !== env || status.client_id !== clientId || status.instance_id !== instanceId) {
    return { ok: false, error: "mismatch" };
  }
  if (!status.app_version_code || status.app_version_code < MIN_PRINTER_APP_VERSION_CODE) return { ok: false, error: "outdated" };
  if (!status.printer_connected) return { ok: false, error: "no_printer" };
  if (!status.printing_enabled) return { ok: false, error: "printing_disabled" };
  return { ok: true };
}
